interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface ingredients {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    // ingredients: ingredients;
    // treatmentAreas1: TreatmentArea[];
    // treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const vitaminc: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "vitamin-c-iv-drip",
        "meta": {
            "title": "Vitamin C IV Drip in Dubai | Elite Body Home",
            "description": "Boost immunity and skin health with our high-dose Vitamin C IV therapy that delivers nutrients directly to your bloodstream for maximum absorption.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/vitamin-c-iv-drip/",
            "og": {
                "title": "Vitamin C IV Drip in Dubai | Elite Body Home",
                "description": "High-dose Vitamin C therapy for immune support and skin health",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-51.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Vitamin C IV Drip"],
        "hero": {
            "title": "Vitamin C IV Drip in Dubai",
            "subtitle": "Immune Support & Skin Health Therapy",
            "description": "Vitamin C is a powerful antioxidant and essential nutrient known to boost the immune system. Most people turn to food and supplements to consume their daily dose of Vitamin C, yet the Vitamin C IV drip is a more direct and effective delivery method. The rapid growth of this treatment can be explained by its health benefits; it boasts of boosting the immune system, promoting skin health, and hastening recovery from illness."
        },
        "howItWorks": {
            "title": "How Does Vitamin C IV Drip Work?",
            "description": "Vitamin C IV drip is an infusion that introduces high quantities of vitamin C into the bloodstream through an intravenous (IV) line. This way, the vitamin gets into your system quicker so the body absorbs and uses it immediately without being hampered by the digestive system, meaning you are getting a concentrated dose of Vitamin C."
        },
        "procedureSteps": {
            "heading": "Procedure Overview",
            "para": "We offer a simple and minimally invasive Vitamin C IV drip procedure that typically takes 30 to 60 minutes:",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "An evaluation by your healthcare provider to determine whether the drip is safe and appropriate for your health goals.",
                    "icon": "fa-solid fa-clipboard-question"
                },
                {
                    "title": "Preparation",
                    "desc": "The healthcare professional cleans and sterilizes the injection site, typically in the arm, and inserts a small needle connected to the IV drip bag.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Administration",
                    "desc": "The Vitamin C solution gradually drips into your bloodstream over 30 to 60 minutes.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Monitoring",
                    "desc": "You'll be seated comfortably under supervision with your vital signs monitored throughout the session.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "Post-Treatment",
                    "desc": "After completion, the needle is removed and you can resume normal activities immediately.",
                    "icon": "fa-solid fa-check-circle"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Vitamin C IV Drip",
            "subheading": "Experience enhanced immunity and skin health",
            "para": "Vitamin C IV drips provide many potential health benefits which is what makes it a very attractive option for a great majority of people.",
            "items": [
                {
                    "title": "Boosts Immune Function",
                    "desc": "Vitamin C in large doses boosts immunity and makes it easier to prevent colds and flu.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "Promotes Skin Health",
                    "desc": "Plays a crucial role in collagen production, reducing wrinkles and enhancing skin elasticity for a more youthful appearance.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Speeds Up Recovery",
                    "desc": "Reduces inflammation and helps you recover from illness and physical stress more quickly.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "Antioxidant Defense",
                    "desc": "Fights free radicals, protecting cells from oxidative stress in the body.",
                    "icon": "fa-solid fa-leaf"
                },
                {
                    "title": "Improves Energy Levels",
                    "desc": "Many people feel more energetic and less fatigued after receiving a Vitamin C IV drip.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Aids in Detoxification",
                    "desc": "High-dose Vitamin C helps flush the system as an overall detox agent.",
                    "icon": "fa-solid fa-recycle"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Can Benefit from Vitamin C IV Drip?",
            "subheading": "",
            "para": "Not everyone is suitable for a Vitamin C IV drip. You should see a health professional to find out whether this treatment is appropriate for you.",
            "items": [
                {
                    "title": "Immune Support Seekers",
                    "desc": "Are trying to strengthen your immune system.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "Skin Health Conscious",
                    "desc": "Looking for ways to enhance the health and look of your skin.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Post-Surgery/Illness",
                    "desc": "Have had surgery or are ill and want to recover faster.",
                    "icon": "fa-solid fa-procedures"
                },
                {
                    "title": "Chronic Fatigue Sufferers",
                    "desc": "Feel tired all the time or feel fatigued.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "Vitamin C Deficiency",
                    "desc": "Experience a Vitamin C deficiency that does not resolve with oral supplementation.",
                    "icon": "fa-solid fa-capsules"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation Before Your Treatment",
            "subheading": "",
            "para": "Vitamin C IV Drip Procedure Preparation Guide Before the procedure:",
            "items": [
                {
                    "title": "Medical Consultation",
                    "desc": "Disclose your medical history and regular medications to prevent any interactions or side effects.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "Hydration",
                    "desc": "Hydrate yourself before the appointment as good hydration keeps veins active for easier insertion.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Light Meal",
                    "desc": "Eat a small meal before your treatment to prevent lightheadedness.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Comfortable Clothing",
                    "desc": "Wear loose fitting attire that exposes your arm easily for IV placement.",
                    "icon": "fa-solid fa-tshirt"
                },
                {
                    "title": "Relaxation",
                    "desc": "Relax before and during the procedure for a pleasant experience.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "results": {
            "heading": "Expected Results",
            "subheading": "",
            "para": "The effects of a Vitamin C IV drip can show up in a few hours to a few days in most people, with benefits including increased energy, healthy skin, modulated immune function, and improved recovery.",
            "items": [
                {
                    "title": "Immediate Benefits",
                    "desc": "Many experience heightened energy, mental clarity, and less fatigue in the days immediately after the session.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Long-Term Benefits",
                    "desc": "With routine treatments: improved skin tone, enhanced immune response and overall wellness.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Customizable Results",
                    "desc": "Can be combined with other nutrients to target specific areas like skin health or detox.",
                    "icon": "fa-solid fa-sliders-h"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Recommendations",
            "subheading": "",
            "para": "To maximize and maintain the benefits of your Vitamin C IV Drip:",
            "items": [
                {
                    "title": "Stay Hydrated",
                    "desc": "Drink plenty of water to help your body utilize the nutrients effectively.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Monitor Effects",
                    "desc": "Pay attention to how your body responds to the treatment.",
                    "icon": "fa-solid fa-clipboard-check"
                },
                {
                    "title": "Follow-up Sessions",
                    "desc": "Consider regular treatments for sustained benefits as recommended by our professionals.",
                    "icon": "fa-solid fa-calendar-alt"
                },
                {
                    "title": "Healthy Lifestyle",
                    "desc": "Maintain a balanced diet and adequate rest to complement the treatment effects.",
                    "icon": "fa-solid fa-heart"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long does a Vitamin C IV Drip session take?",
                    "answer": "The treatment typically takes between 30 to 60 minutes depending on the dosage and your individual needs."
                },
                {
                    "question": "Is Vitamin C IV therapy painful?",
                    "answer": "You may feel a small pinch when the IV is inserted, but the infusion itself is painless."
                },
                {
                    "question": "How soon will I notice results?",
                    "answer": "Many patients report feeling increased energy and mental clarity within hours to a few days after treatment."
                },
                {
                    "question": "How often should I get Vitamin C IV therapy?",
                    "answer": "Frequency depends on your health goals - our healthcare providers can recommend a personalized schedule during consultation."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Side effects are rare but may include minor bruising at the injection site or temporary flushing."
                },
                {
                    "question": "Can I get Vitamin C IV Drip if I'm pregnant?",
                    "answer": "Pregnant women should consult their obstetrician before receiving any IV therapy."
                },
                {
                    "question": "What's the difference between oral Vitamin C and IV Vitamin C?",
                    "answer": "IV delivery bypasses digestive limitations, allowing for higher absorption rates and more immediate effects compared to oral supplements."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Banner-dfsefImage-1-scaled.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 8,
            "title": "Vitamin C IV Drip in Dubai",
            "description": "Boost immunity and skin health with our high-dose Vitamin C IV therapy that delivers nutrients directly to your bloodstream for maximum absorption.",
            "price": 599
        }
    },
    "ar": {
        "url": "vitamin-c-iv-drip",
        "meta": {
            "title": "التنقيط الوريدي بفيتامين سي في دبي | ايليت بودي هوم",
            "description": "عزز مناعتك وصحة بشرتك مع علاج فيتامين سي الوريدي عالي التركيز الذي يصل مباشرة إلى مجرى الدم لأقصى امتصاص.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/vitamin-c-iv-drip",
            "og": {
                "title": "التنقيط الوريدي بفيتامين سي في دبي | ايليت بودي هوم",
                "description": "علاج فيتامين سي الوريدي عالي الجرعة لدعم المناعة وصحة البشرة",
                "image": "https://www.elitebodyhome.com/images/vitamin-c-iv-og.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "التنقيط الوريدي بفيتامين سي"],
        "hero": {
            "title": "التنقيط الوريدي بفيتامين سي",
            "subtitle": "علاج دعم المناعة وصحة البشرة",
            "description": "يُعد فيتامين سي مضاد أكسدة قوي وعنصر أساسي للجسم، يُعرف بدوره الفعال في تعزيز جهاز المناعة. وعلى الرغم من أن معظم الأشخاص يحصلون عليه من خلال الطعام أو المكملات الغذائية، إلا أن علاج فيتامين C الوريدي يُعد وسيلة أكثر فاعلية وسرعة. ويعود الانتشار السريع لهذا العلاج إلى فوائده الصحية المتعددة، حيث يُعزز مناعة الجسم، ويدعم صحة البشرة، ويسرع عملية التعافي من الأمراض، والتئام الجروح."
        },
        "howItWorks": {
            "title": "كيف يعمل التنقيط الوريدي بفيتامين سي؟",
            "description": "يُجرى التنقيط الوريدي بفيتامين سي من خلال إدخال كميات عالية منه مباشرةً إلى مجرى الدم عبر الوريد. يصل الفيتامين إلى الجسم بسرعة بهذه الطريقة، ويتم امتصاصه واستخدامه بشكل فوري دون أن يمر عبر الجهاز الهضمي، مما يعني أنك تحصل على جرعة مركزة من فيتامين سي."
        },
        "procedureSteps": {
            "heading": "خطوات الإجراء",
            "para": "نقدم لك جلسة تنقيط فيتامين سي الوريدي بطريقة بسيطة وغير جراحية، تُجرى عادةً في بيئة طبية وتحت إشراف أحد المتخصصين:",
            "steps": [
                {
                    "title": "استشارة الطبيب",
                    "desc": "يبدأ الإجراء بتقييم شامل من الطبيب، لتحديد مدى ملاءمة العلاج لحالتك الصحية وأهدافك، بناءً على تاريخك الطبي.",
                    "icon": "fa-solid fa-clipboard-question"
                },
                {
                    "title": "التحضير",
                    "desc": "يقوم الأخصائي بتنظيف وتعقيم منطقة الحقن، التي غالبًا ما تكون في الذراع، ثم إدخال إبرة صغيرة متصلة بمحلول فيتامين سي.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "التنقيط الوريدي",
                    "desc": "يُحقن المحلول تدريجيًا في مجرى الدم خلال 30 إلى 60 دقيقة.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "المتابعة",
                    "desc": "ستجلس في وضع مريح وتبقى تحت إشراف طبي لمراقبة العلامات الحيوية أثناء الجلسة.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "ما بعد الجلسة",
                    "desc": "بعد الانتهاء، تُزال الإبرة ويمكنك متابعة أنشطتك اليومية بشكل طبيعي.",
                    "icon": "fa-solid fa-check-circle"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد العلاج",
            "subheading": "استمتع بمناعة أقوى وبشرة أكثر صحة",
            "para": "يوفر علاج فيتامين سي الوريدي فوائد صحية متعددة، مما يجعله خيار جذاب للعديد من الأشخاص.",
            "items": [
                {
                    "title": "تعزيز جهاز المناعة",
                    "desc": "تعزز الجرعات العالية من فيتامين سي مناعة الجسم، ويساعده على مقاومة نزلات البرد والإنفلونزا.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "تحسين صحة البشرة",
                    "desc": "يلعب فيتامين سي دور أساسي في تحفيز إنتاج الكولاجين، مما يُساهم في تقليل التجاعيد، وتحسين مرونة البشرة، ومنحها مظهرًا أكثر شبابًا.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "تسريع عملية التعافي",
                    "desc": "يساعد فيتامين سي على تقليل الالتهابات، مما يُسرع عملية الشفاء سواء من الأمراض أو من الإجهاد البدني الشديد.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "دعم مضادات الأكسدة",
                    "desc": "يحارب فيتامين سي الجذور الحرة التي قد تضر الخلايا كونه من أقوى مضادات الأكسدة، مما يقي من الإجهاد التأكسدي داخل الجسم.",
                    "icon": "fa-solid fa-leaf"
                },
                {
                    "title": "تحسين مستويات الطاقة",
                    "desc": "يلاحظ كثير من الأشخاص تحسنًا في مستويات الطاقة وتقليل الشعور بالإرهاق بعد تلقي علاج فيتامين سي الوريدي.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "المساعدة في التخلص من السموم",
                    "desc": "تساهم الجرعات العالية من فيتامين سي في تنظيف الجسم، حيث يعمل كمحفز طبيعي لعملية إزالة السموم.",
                    "icon": "fa-solid fa-recycle"
                }
            ]
        },
        "eligibility": {
            "heading": "من هو المرشح المناسب للعلاج؟",
            "subheading": "",
            "para": "لا يناسب علاج فيتامين سي الوريدي الجميع، ولذلك يجب استشارة الطبيب لتحديد ما إذا كان هذا العلاج مناسبًا لحالتك الصحية.",
            "items": [
                {
                    "title": "الراغبون في تعزيز المناعة",
                    "desc": "إذا كنت تسعى إلى تعزيز جهازك المناعي.",
                    "icon": "fa-solid fa-shield-virus"
                },
                {
                    "title": "المهتمون بصحة البشرة",
                    "desc": "إذا كنت تبحث عن طرق لتحسين صحة ومظهر بشرتك.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "المتعافون من جراحة أو مرض",
                    "desc": "إذا كنت تمر بمرحلة تعافٍ من جراحة أو مرض وتريد تسريع عملية الشفاء.",
                    "icon": "fa-solid fa-procedures"
                },
                {
                    "title": "المعانون من الإرهاق المستمر",
                    "desc": "إذا كنت تعاني من الإرهاق المستمر أو تشعر بالإجهاد.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "المصابون بنقص فيتامين سي",
                    "desc": "إذا كنت تعاني من نقص في فيتامين سي لا يتحسن بالمكملات الغذائية التي تؤخذ عن طريق الفم.",
                    "icon": "fa-solid fa-capsules"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد قبل الجلسة",
            "subheading": "",
            "para": "يُفضل اتباع الخطوات التالية قبل الخضوع للجلسة:",
            "items": [
                {
                    "title": "استشارة طبية",
                    "desc": "أخبر طبيبك بتاريخك الطبي والأدوية التي تتناولها بانتظام، لتجنب أي تداخلات دوائية أو آثار جانبية محتملة.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "اشرب كمية كافية من الماء قبل الموعد، حيث يساعد ترطيب الجسم في الحفاظ على نشاط الأوردة، مما يسهل عملية إدخال الإبرة الوريدية.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "وجبة خفيفة",
                    "desc": "يساعد تناول وجبة خفيفة قبل الجلسة في منع الشعور بالدوار أثناء العلاج.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "ملابس مريحة",
                    "desc": "اختر ملابس واسعة تُسهّل الوصول إلى الذراع، وهو الموضع الذي يتم فيه وضع الإبرة الوريدية.",
                    "icon": "fa-solid fa-tshirt"
                },
                {
                    "title": "الاسترخاء",
                    "desc": "حاول أن تكون مسترخيًا قبل وأثناء الجلسة لتجربة مريحة وسلسة.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "",
            "para": "تظهر نتائج علاج التنقيط الوريدي بفيتامين سي خلال بضع ساعات إلى بضعة أيام لدى أغلب الأشخاص، مع فوائد تشمل زيادة الطاقة، وتحسن نضارة البشرة، وتقوية المناعة، وتحسّن التعافي.",
            "items": [
                {
                    "title": "نتائج فورية",
                    "desc": "يشعر العديد من الأشخاص بارتفاع في مستوى الطاقة، وتحسّن في صفاء الذهن، وانخفاض ملحوظ في الشعور بالإرهاق خلال الأيام التالية مباشرةً للجلسة.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "فوائد طويلة الأمد",
                    "desc": "مع الجلسات المنتظمة: تحسن عام في مظهر البشرة، واستجابة مناعية أقوى، وشعور عام بالصحة والنشاط.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "نتائج قابلة للتخصيص",
                    "desc": "يمكن دمج فيتامين سي الوريدي مع عناصر غذائية أخرى لتتناسب مع أهدافك الصحية المحددة مثل نضارة البشرة أو التخلص من السموم.",
                    "icon": "fa-solid fa-sliders-h"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح ما بعد العلاج",
            "subheading": "",
            "para": "لتعزيز والحفاظ على فوائد علاج فيتامين سي الوريدي:",
            "items": [
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "اشرب كميات كافية من الماء لمساعدة جسمك على الاستفادة من العناصر الغذائية بشكل فعال.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "مراقبة التأثيرات",
                    "desc": "انتبه لاستجابة جسمك للعلاج.",
                    "icon": "fa-solid fa-clipboard-check"
                },
                {
                    "title": "جلسات متابعة",
                    "desc": "فكر في الجلسات المنتظمة للحصول على فوائد مستدامة حسب توصية أخصائينا.",
                    "icon": "fa-solid fa-calendar-alt"
                },
                {
                    "title": "نمط حياة صحي",
                    "desc": "حافظ على نظام غذائي متوازن وراحة كافية لتعزيز تأثيرات العلاج.",
                    "icon": "fa-solid fa-heart"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "كم تستغرق جلسة فيتامين سي الوريدي؟",
                    "answer": "يستغرق العلاج عادةً ما بين 30 إلى 60 دقيقة حسب الجرعة واحتياجاتك الفردية."
                },
                {
                    "question": "هل العلاج مؤلم؟",
                    "answer": "قد تشعر بوخز بسيط عند إدخال الإبرة، ولكن التسريب نفسه غير مؤلم."
                },
                {
                    "question": "متى سألاحظ النتائج؟",
                    "answer": "يذكر العديد من المرضى شعورًا بزيادة الطاقة وصفاء الذهن خلال ساعات إلى أيام بعد العلاج."
                },
                {
                    "question": "كم مرة يجب أن أحصل على هذا العلاج؟",
                    "answer": "يعتمد التكرار على أهدافك الصحية - يمكن لأخصائيي الرعاية الصحية تقديم جدول زمني مخصص خلال الاستشارة."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "الآثار الجانبية نادرة ولكن قد تشمل كدمات طفيفة في موقع الحقن أو احمرار مؤقت."
                },
                {
                    "question": "هل يمكن الحصول على العلاج أثناء الحمل؟",
                    "answer": "يجب على النساء الحوامل استشارة طبيب النساء والتوليد قبل الخضوع لأي علاج وريدي."
                },
                {
                    "question": "ما الفرق بين فيتامين سي الفموي والوريدي؟",
                    "answer": "التسريب الوريدي يتجاوز قيود الامتصاص في الجهاز الهضمي، مما يسمح بمعدلات امتصاص أعلى وتأثيرات أكثر فورية مقارنة بالمكملات الفموية."
                }
            ]
        }
    }
}